<template>
  <div class="add-award">
    <div class="content">
      <div class="head text-center side-gutter">
        <back-button class="back-btn" @back="$emit('back')" />
        <span class="heading bolder text-large">Create a new award</span>
      </div>
      <div class="pl-1 mt-3 badge-section side-gutter mr-0">
        <div class="text-medium-1 bolder">1. Choose a badge</div>
        <ul class="d-flex flex-wrap m-0 p-0 badge-list">
          <li
            v-for="(badge, index) in badgesList"
            :key="'2' + index"
            class="list-badge-item flex-center flex-column pointer"
            @click="onBadgeSelect(badge)"
          >
            <div
              class="list-icon-wrapper flex-center"
              :class="{ selected: selectedBadge && badge.icon === selectedBadge.icon }"
            >
              <svg-sprite-icon :id="badge.icon" size="30" />
            </div>
          </li>
        </ul>
      </div>
      <hr class="px-3 my-0 side-gutter" />
      <div class="pl-1 mt-3 side-gutter">
        <div class="text-medium-1 bolder">2. Give it a name</div>
        <div class="d-flex mt-2">
          <div class="selected-badge flex-center">
            <svg-sprite-icon :id="selectedBadge.icon" v-if="selectedBadge" />
            <span class="badge-placeholer text-center text-secondary" v-else>Choose above</span>
          </div>
          <div class="award-name" :class="{ errored: errors.caption }">
            <input
              maxlength="30"
              ref="caption"
              class="award-name-field text-medium-1"
              :class="{ 'text-gradient bolder': caption.length > 0 }"
              placeholder="e.g. Milestone Maker, Mountain Mover, Creative Cat, Above & Beyond"
              v-model="caption"
              @keyup.enter="createAward"
              v-autofocus
            />
          </div>
        </div>
      </div>
      <b-button
        variant="primary"
        pill
        class="px-4 text-medium-1 mt-3 py-1 side-gutter"
        @click="createAward"
        >Create and use this Award
      </b-button>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import BackButton from '@/components/BackButton'
import { BADGES_LIST } from '@/constants/badges'
import SvgSpriteIcon from '@/components/SvgSpriteIcon'

/**
 * This is the component for adding a custom badge with caption for giving an award
 *
 * @emits
 *  - create
 */
export default {
  name: 'AddAward',
  components: {
    BackButton,
    BButton,
    SvgSpriteIcon
  },
  data() {
    return {
      selectedBadge: BADGES_LIST[0],
      caption: '',
      badgesList: BADGES_LIST,
      errors: {}
    }
  },
  methods: {
    onBadgeSelect(badge) {
      this.selectedBadge = badge
      this.focusCaption()
    },
    focusCaption() {
      this.$refs.caption.focus()
    },
    createAward() {
      this.submitted = true
      if (this.caption.length === 0) {
        this.errors = { caption: true }
        this.focusCaption()
        return
      }
      this.$emit('create', { caption: this.caption, icon: this.selectedBadge.icon })
    }
  },
  watch: {
    caption(caption) {
      this.errors.caption = this.submitted && !caption
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/_errors.scss';

.icon-wrapper {
  @include dimension(65px, 65px);
  background-color: $quinary;
  border-radius: 50%;
  position: relative;

  &:after {
    content: ' ';
    position: absolute;
    border: 5px solid transparent;
    border-top-color: $prime;
    right: -20px;
    top: 50%;
    transform: translateX(-50%);
  }
}

.add-award {
  height: 450px;
  width: 540px;
  padding-top: 20px;
}

.head {
  position: relative;
}

.back-btn {
  position: absolute;
  left: 0;
}

.badge-list {
  @include scrollbarWidth($scrollSecondary);
  max-height: 204px;
  overflow: auto;
}

.list-badge-item {
  @include dimension(60px, 60px);
  margin-bottom: 8px;
  margin-right: 20px;
  border-radius: 10px;

  &:nth-child(6n) {
    margin-right: 0;
  }

  &:hover .list-icon-wrapper,
  .list-icon-wrapper.selected {
    background-color: $activeQuaternary;
  }
}

.list-icon-wrapper {
  @include dimension(50px, 50px);
  background-color: $quinary;
  border-radius: 50%;
  transition: background-color ease-in 100ms;

  i {
    @include dimension(28px, 28px);
  }

  &.selected {
    border: 3px solid $active;
  }
}

.selected-badge {
  @extend .list-icon-wrapper;
  @include dimension(65px, 65px);

  i {
    @include dimension(36px, 36px);
  }
}

.award-name {
  border-radius: 20px;
  background-color: $quinary;
  align-self: center;
  padding: 8px 19px;
  margin-left: 5px;
  flex-grow: 1;
}

.award-name.errored {
  border: 1px solid $errorSecondary;
}

.side-gutter {
  margin: 0 20px;
}
.award-name-field {
  border: none;
  background-color: $quinary;
  width: 100%;

  &::placeholder {
    font-size: 0.7rem;
  }
}
</style>
